import CryptoJS from 'crypto-js';
import dayjs, { Dayjs } from 'dayjs';
import { Division } from 'src/app/@core/domain/interfaces/division.interface';
import { environment } from 'src/environments/environment';
import { Facility } from 'src/app/@core/domain/interfaces/facility.interface';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { NbThemeService } from '@nebular/theme';
import { StorageService } from 'ngx-webstorage-service';
import { User } from 'src/app/@core/domain/models/user.model';
import {
  Definition,
  DefinitionList,
  FilterSet,
} from 'src/app/@core/domain/interfaces/definition.interface';
import {
  SearchResultItem,
  SearchResultType,
} from '../../@core/domain/interfaces/search-result.interface';
import { CookieOptions, CookieService } from 'ngx-cookie-service';

export interface FacilitiesChoice {
  selected: boolean;
  facility: Facility;
}

const ENCRYPT = false;

const cookieOptions: CookieOptions = {
  expires: 312,
  path: '/',
  secure: true,
  sameSite: 'Lax',
};

export const encryptData = (data: string, salt: string) => {
  if (ENCRYPT) {
    CryptoJS.AES.encrypt(JSON.stringify(data), salt).toString();
  } else {
    return data;
  }
  return '';
};

export const decryptData = (ciphertext: string, salt: string) => {
  if (ENCRYPT) {
    const bytes = CryptoJS.AES.decrypt(ciphertext, salt);
    try {
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      return null;
    }
  } else {
    return ciphertext;
  }
};

const SALT = '6f0ac317-93c9-4136-bd85-8a788147bb6b-1086861e-4130-40d1-a345-362f46809951';

export const PARKBASE_SERVICE_STORAGE = new InjectionToken<StorageService>(
  'PARKBASE_SERVICE_STORAGE'
);

export interface NotificationTypeFilter {
  id: number;
  name: string;
  checked: boolean;
}

/**
 * Stores static information like definition lists, user data and user preferences in session storage
 * After being loaded no API calls for this information needed
 * Can be switched to use local storage in AppModule
 *
 */
@Injectable({
  providedIn: 'root',
})
export class ParkbaseStorageService {
  defaultFromDate = dayjs().set('hour', 23).set('minute', 59).set('second', 59).subtract(29, 'day');
  private userKey!: string;
  private firstUse = true;

  constructor(
    @Inject(PARKBASE_SERVICE_STORAGE) private storage: StorageService,
    private themeService: NbThemeService,
    private cookieService: CookieService
  ) {
    const STORAGE_KEY = 'PBStoreVersion';
    const appVersion = environment.version;
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.clear();
      this.storage.set(STORAGE_KEY, encryptData(appVersion, SALT));
    } else {
      const currentVersion = decryptData(this.storage.get(STORAGE_KEY), SALT);
      if (currentVersion !== appVersion) {
        this.storage.clear();
        this.storage.set(STORAGE_KEY, encryptData(appVersion, SALT));
      }
    }
  }

  /**
   * Get API version for inclusion in reporting and logs
   */
  retrieveApiVersion(): any {
    const STORAGE_KEY = 'PBApiVersion';
    const storeVersion = decryptData(this.storage.get('PBStoreVersion'), SALT);
    return storeVersion + '-' + decryptData(this.storage.get(STORAGE_KEY), SALT);
  }

  storeApiVersion(version: string): void {
    const STORAGE_KEY = 'PBApiVersion';
    this.storage.remove(STORAGE_KEY);
    this.storage.set(STORAGE_KEY, encryptData(version, SALT));
  }

  /**
   * Layout of the status page:
   * 0: n facilities <= 3
   * 1: n facilities > 3 && < 15 default
   * 2: n facilities >= 15
   */
  retrieveStatusPageLayout(): any {
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      return decryptData(userStore['PBFacilityLayout'], SALT);
    }
  }

  storeStatusPageLayout(layout: string): void {
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      userStore['PBFacilityLayout'] = encryptData(layout, SALT);
      this.storage.set(this.userKey, userStore);
    }
  }

  /**
   * Format of date time in alarm notifications
   * 1: Relative: 15 minutes ago, 12 days ago
   * 2: Exact: 12:03:45 10-01-2022
   */
  storeDateformatChoice(choice: string) {
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      userStore['PBDateformatChoice'] = encryptData(choice, SALT);
      this.storage.set(this.userKey, userStore);
    }
  }

  retrieveDateformatChoice() {
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      return decryptData(userStore['PBDateformatChoice'], SALT);
    }
  }

  disableFirstUse() {
    this.cookieService.delete('firstUse');
    this.cookieService.set('firstUse', 'false', cookieOptions);
    this.firstUse = false;
    return this.firstUse;
  }

  enableFirstUse() {
    this.cookieService.delete('firstUse');
    this.cookieService.set('firstUse', 'true', cookieOptions);
    this.firstUse = true;
    return this.firstUse;
  }

  handleFirstUseCookie(): boolean {
    const firstUseCookieExists = this.cookieService.check('firstUse');
    if (firstUseCookieExists) {
      return this.cookieService.get('firstUse') === 'true';
    } else {
      this.cookieService.set('firstUse', 'true', cookieOptions);
      this.firstUse = true;
      return this.firstUse;
    }
  }

  /**
   * Search history used in global search
   */
  storeRecentSearches(recentSearches: string[]): void {
    const STORAGE_KEY = 'PBRecentSearches';
    this.storage.remove(STORAGE_KEY);
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(recentSearches), SALT));
  }

  /**
   * Search history used in global search
   */
  storeRecentSearch(searchTerm: string): void {
    const STORAGE_KEY = 'PBRecentSearches';
    let previousSearchTerms: string[] = this.retrieveRecentSearches();
    if (previousSearchTerms && previousSearchTerms.length > 0) {
      previousSearchTerms.unshift(searchTerm);
    } else {
      previousSearchTerms = [];
      previousSearchTerms.unshift(searchTerm);
    }
    previousSearchTerms = previousSearchTerms.filter((el, i, a) => i === a.indexOf(el));
    this.storage.remove(STORAGE_KEY);
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(previousSearchTerms), SALT));
  }

  retrieveRecentSearches(): string[] {
    const STORAGE_KEY = 'PBRecentSearches';
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.set(STORAGE_KEY, encryptData(JSON.stringify([]), SALT));
    }
    const searchItems = JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
    if (searchItems) {
      return searchItems;
    }
    return [];
  }

  /**
   * The storage
   */
  getStorage(): StorageService<any> {
    return this.storage;
  }

  /** UserSessionId */
  storeUserSessionId(userProfile: User): void {
    const STORAGE_KEY = 'PBUserSessionId';
    this.userKey = userProfile.key;
    this.storage.remove(STORAGE_KEY);
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(userProfile), SALT));
    this.setDefaultUserstore();
    // Load default theme or theme selection stored by user
    const selectedTheme = this.getTheme();
    this.themeService.changeTheme(selectedTheme);
  }

  setDefaultUserstore() {
    let userStore = this.storage.get(this.userKey);
    if (!userStore) {
      userStore = {
        PBDateformatChoice: encryptData('Relative', SALT),
        PBFacilityLayout: encryptData('Extended', SALT),
        PBTheme: encryptData('parkbaseDark', SALT),
        PBLang: encryptData('nl-NL', SALT),
      };
      this.storage.set(this.userKey, userStore);
    }
  }

  clearUserSessionId(): void {
    const STORAGE_KEY = 'PBUserSessionId';
    this.storage.remove(STORAGE_KEY);
  }

  retrieveUserSessionId(): User {
    const STORAGE_KEY = 'PBUserSessionId';
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.set(STORAGE_KEY, encryptData(JSON.stringify({}), SALT));
    }
    return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
  }

  /** Division */
  storeDivision(division: Division): void {
    const STORAGE_KEY = 'PBCurrentDivision';
    this.storage.remove(STORAGE_KEY);
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(division), SALT));
  }

  retrieveDivision(): any {
    const STORAGE_KEY = 'PBCurrentDivision';
    const division = JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
    return division.name + ' ' + division.city;
  }

  /** Facilities */
  storeFacilities(facilities: Facility[]): void {
    const STORAGE_KEY = 'PBFacilities';
    this.storage.remove(STORAGE_KEY);
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(facilities), SALT));
    const choice = this.retrieveFacilitiesChoice();
    if (choice && choice.length === 0) {
      const facilitiesChoiceOptions: any[] = [];
      facilities.forEach((f) => {
        facilitiesChoiceOptions.push({ selected: true, facility: f });
      });
      this.storeFacilitiesChoice(facilitiesChoiceOptions);
    }
  }

  retrieveFacilities(): Facility[] {
    const STORAGE_KEY = 'PBFacilities';
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.set(STORAGE_KEY, encryptData(JSON.stringify([]), SALT));
    }
    return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
  }

  // FacilitySelection
  storeFacilitySelection(facility: Facility): void {
    const STORAGE_KEY = 'PBFacilitySelected';
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(facility), SALT));
  }

  retrieveFacilitySelection(): Facility {
    const STORAGE_KEY = 'PBFacilitySelected';
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.set(STORAGE_KEY, encryptData(JSON.stringify({}), SALT));
    }
    return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
  }

  // Definitions
  storeDefinitionLists(data: DefinitionList[]): void {
    const STORAGE_KEY = 'PBDefinitionLists';
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(data), SALT));
  }

  retrieveDefinitionList(type: number): Definition[] {
    const STORAGE_KEY = 'PBDefinitionLists';
    const defintionLists: DefinitionList[] = JSON.parse(
      decryptData(this.storage.get(STORAGE_KEY), SALT)
    );
    return defintionLists[type].definitions;
  }

  // User filterSet
  retrieveFilterSet(): FilterSet {
    const STORAGE_KEY = 'PBNotificationTypeFilterSet';
    if (this.storage.has(STORAGE_KEY)) {
      return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
    } else {
      return { from: this.defaultFromDate, filtersSelected: [] };
    }
  }

  prepareNotificationTypeFilterSet(from: Dayjs, definitions: Definition[]): FilterSet {
    const STORAGE_KEY = 'PBNotificationTypeFilterSet';
    if (!this.storage.has(STORAGE_KEY)) {
      const filterSet: FilterSet = { from, filtersSelected: [] };
      definitions.forEach((d) => {
        filterSet.filtersSelected.push({
          id: d.id,
          name: d.name,
          checked: false,
        });
      });
      this.storage.set(
        STORAGE_KEY,
        encryptData(
          JSON.stringify({
            filtersSelected: filterSet.filtersSelected,
            from: from.format().toString(),
          }),
          SALT
        )
      );
    }
    return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
  }

  saveNotificationTypeFilterSet(filterSet: FilterSet): void {
    const STORAGE_KEY = 'PBNotificationTypeFilterSet';
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(filterSet), SALT));
  }

  storeNotificationLevelFilterSet(levels: number[]): any {
    const STORAGE_KEY = 'PBNotificationLevelFilterSet';
    const filterSet: number[] = [];
    levels.forEach((d) => {
      filterSet.push(d);
    });
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify(filterSet), SALT));
    return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
  }

  retrieveNotificationLevelFilterSet(): any {
    const STORAGE_KEY = 'PBNotificationLevelFilterSet';
    return JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
  }

  /** Set global styling theme */
  setTheme(theme: string): void {
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      userStore['PBTheme'] = encryptData(theme, SALT);
      this.storage.set(this.userKey, userStore);
    }
  }

  /** Get global styling theme */
  getTheme(): string {
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      return decryptData(userStore['PBTheme'], SALT);
    } else {
      return 'parkbaseDark';
    }
  }

  /**
   * Set language code
   */
  setLanguage(lang: string): void {
    this.cookieService.set('lang', lang, cookieOptions);
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      userStore['PBLang'] = encryptData(lang, SALT);
      this.storage.set(this.userKey, userStore);
    }
  }

  /**
   * Get language code
   */
  getLanguage(): string {
    const storedUser = this.retrieveUserSessionId();
    if (storedUser.key) {
      this.userKey = storedUser.key;
    }
    if (this.userKey) {
      const userStore = this.storage.get(this.userKey);
      return decryptData(userStore['PBLang'], SALT);
    }
    const lang = this.cookieService.get('lang');
    if (lang) {
      return lang;
    }
    return 'en-US';
  }

  /**
   * Language code as numeric string for use in Api header Ipp-Language
   */
  getLanguageForApi(): string {
    const language = this.getLanguage();
    switch (language) {
      case 'en-GB':
        return '0';
      case 'en-US':
        return '1';
      case 'en-AU':
        return '2';
      case 'nl-NL':
        return '3';
      case 'de-DE':
        return '4';
      case 'fr-FR':
        return '5';
      case 'sv-SE':
        return '6';
      case 'sv-FI':
        return '7';
      default:
        return '3';
    }
  }

  storeSearchResult(searchResult: SearchResultItem, searchResultType: SearchResultType): void {
    const STORAGE_KEY = 'PBSearchResultItem';
    this.storage.set(
      STORAGE_KEY,
      encryptData(JSON.stringify({ searchResult, searchResultType }), SALT)
    );
  }

  retrieveSearchResult(): SearchResult {
    const STORAGE_KEY = 'PBSearchResultItem';
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.set(STORAGE_KEY, encryptData(JSON.stringify({}), SALT));
    }
    return decryptData(this.storage.get(STORAGE_KEY), SALT);
  }

  storeFacilitiesChoice(facilitiesChoice: FacilitiesChoice[]) {
    const STORAGE_KEY = 'PBFacilitiesChoice';
    this.storage.set(STORAGE_KEY, encryptData(JSON.stringify({ facilitiesChoice }), SALT));
  }

  retrieveFacilitiesChoice(): FacilitiesChoice[] {
    const STORAGE_KEY = 'PBFacilitiesChoice';
    if (!this.storage.has(STORAGE_KEY)) {
      this.storage.set(STORAGE_KEY, encryptData(JSON.stringify({ facilitiesChoice: [] }), SALT));
    }
    const choice = JSON.parse(decryptData(this.storage.get(STORAGE_KEY), SALT));
    return choice.facilitiesChoice;
  }
}

type SearchResult = {
  searchResult: SearchResult;
  searchResultType: SearchResultType;
};
